import { EMERGENCY_TYPES } from "../EmergencyTypeGrid";
import { getCountryConfig } from "../../lib/countryConfig";

// Module-level chart constants — hoisted out of the component render
// closure so we don't allocate new object references on every paint
// (Recharts diff-compares these and re-renders descendants otherwise).
export const CHART_MARGIN = { top: 4, right: 4, bottom: 4, left: 0 };
export const CHART_TICK   = { fontSize: 9, fill: "#71717a" };
export const CHART_TOOLTIP_CONTENT = { background: "#0b0f14", border: "1px solid #27272a", fontSize: 11 };
export const CHART_TOOLTIP_LABEL   = { color: "#a1a1aa" };
export const CHART_TOOLTIP_ITEM    = { color: "#10B981" };
export const CHART_TOOLTIP_FORMATTER = (v) => [v, "Alertes"];

// Initial view centred on the active country (Mali by default). Reads
// from the country config so the same component renders correctly when
// the same image is deployed for SOS Guinea, SOS Niger, etc.
const _countryMap = (getCountryConfig() || {}).map || {};
export const MALI_CENTRE = {
  lng: typeof _countryMap.center_lng === "number" ? _countryMap.center_lng : -2.5,
  lat: typeof _countryMap.center_lat === "number" ? _countryMap.center_lat : 17.5,
};
export const MALI_INIT_ZOOM =
  typeof _countryMap.initial_zoom === "number" ? _countryMap.initial_zoom : 4.6;

// ---------- Time range presets (epoch-now relative) ----------
export const RANGES = [
  { id: "1h",   label: "1 H",   ms: 1 * 60 * 60 * 1000 },
  { id: "6h",   label: "6 H",   ms: 6 * 60 * 60 * 1000 },
  { id: "24h",  label: "24 H",  ms: 24 * 60 * 60 * 1000 },
  { id: "3d",   label: "3 J",   ms: 3 * 24 * 60 * 60 * 1000 },
  { id: "7d",   label: "7 J",   ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "30d",  label: "30 J",  ms: 30 * 24 * 60 * 60 * 1000 },
];

export const REFRESH_OPTIONS = [
  { id: "off", label: "OFF", ms: 0 },
  { id: "10s", label: "10 s", ms: 10_000 },
  { id: "30s", label: "30 s", ms: 30_000 },
];

// All 10 emergency types — color + icon imported from the single source of
// truth (EmergencyTypeGrid). Adapted shape: {color, icon} for backward compat.
export const TYPE_META = Object.fromEntries(
  EMERGENCY_TYPES.map(({ key, Icon, accent }) => [key, { color: accent, icon: Icon }])
);

export const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch (_e) { return iso; }
};

// Iter90 — Location Quality Engine (frontend mirror of the backend's
// `classify_location_quality` in app_core.py — same thresholds, display
// purposes only; the backend classification stored on the alert is the
// source of truth, this is just a fallback for older alerts that predate
// the `location_quality` field).
export const QUALITY_LABELS_FR = {
  EXCELLENT: "Excellente",
  GOOD: "Bonne",
  MODERATE: "Moyenne",
  LOW: "Faible",
  VERY_LOW: "Très faible",
  UNKNOWN: "Inconnue",
};
export const QUALITY_COLORS = {
  EXCELLENT: "#10B981",
  GOOD: "#22C55E",
  MODERATE: "#F59E0B",
  LOW: "#F97316",
  VERY_LOW: "#EF4444",
  UNKNOWN: "#71717a",
};
export const classifyLocationQuality = (accuracy) => {
  if (accuracy == null) return "UNKNOWN";
  if (accuracy <= 10) return "EXCELLENT";
  if (accuracy <= 25) return "GOOD";
  if (accuracy <= 50) return "MODERATE";
  if (accuracy <= 100) return "LOW";
  return "VERY_LOW";
};

// Relative "freshness" label for a location timestamp — same 3-tier
// language as the Live Share viewer (EN DIRECT/CONNEXION FAIBLE/
// POSITION PÉRIMÉE) so responders read one consistent vocabulary across
// the whole app, applied here to an alert's `location_updated_at`.
export const fmtLocationFreshness = (iso) => {
  if (!iso) return { label: "—", tier: "unknown" };
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  const ago = s < 60 ? `il y a ${s} s` : s < 3600 ? `il y a ${Math.floor(s / 60)} min` : `il y a ${Math.floor(s / 3600)} h`;
  if (s < 30) return { label: `LIVE · ${ago}`, tier: "live" };
  if (s < 120) return { label: `Récent · ${ago}`, tier: "recent" };
  return { label: `Périmée · ${ago}`, tier: "stale" };
};

export const escapeHtml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// ---------- WebGL availability (graceful fallback) ----------
export const hasWebGL = () => {
  try {
    const c = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext
      && (c.getContext("webgl") || c.getContext("experimental-webgl"))
    );
  } catch (_e) { return false; }
};

// ---------- GeoJSON helpers ----------
export const buildGeoJSON = (points) => ({
  type: "FeatureCollection",
  features: points.map((p) => ({
    type: "Feature",
    properties: {
      id: p.id,
      type: p.type,
      color: TYPE_META[p.type]?.color || "#fff",
      ts: p.ts,
      is_false: !!p.is_false,
      flagged: !!p.flagged,
      reporter_phone: p.reporter_phone || "Anonyme",
      description: p.description || "",
      // Iter90 — GPS audit fields, used by the popup + the accuracy
      // circle layer. `location_quality` may be absent on alerts
      // created before this field existed — falls back client-side.
      accuracy: p.accuracy ?? null,
      location_quality: p.location_quality || classifyLocationQuality(p.accuracy),
      location_updated_at: p.location_updated_at || p.ts,
    },
    geometry: { type: "Point", coordinates: [p.lng, p.lat] },
  })),
});
